import {put, call} from 'redux-saga/effects';
import User from "../../entities/user";
import * as Actions from "../actions/userActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type UserAction = {
    userId: string;
    user: User;
    users: User[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneUser(action: UserAction) {
	const session = tabSession.captureGeneration();
	try {
		const user: User = yield call(api.findOne, 'user', action.userId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.USER_FETCH_ONE_SUCCESS, userId: action.userId, user});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.USER_FETCH_ONE_FAILURE, userId: action.userId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {UserAction} action
 */
export function* saveUser(action: UserAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ USER_SAVE saga', action.user?.id);
    
	if (action.user === undefined || action.user === null) {
		console.error('saveUser received a null');
		throw new Error('saveUser received a null');
	}

	if (!(action.user instanceof User)) {
		action.user = new User(action.user);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'user', action.user);
		if (addResult.isErr()) {
			console.error(`⮂ saveUser: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.USER_SAVE_FAILURE, user: action.user, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.USER_SAVE, user: action.user});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.USER_SAVE_FAILURE, user: action.user, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {UserAction} action
 * @param {User[]} action.users
 */
export function* saveManyUsers(action: UserAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ USER_SAVE_MANY saga', action.users.map(user => user.id).join(','));
    
    if (!Array.isArray(action.users)) {
        console.error('saveManyUsers expected an array, received', action.users);
        throw new Error('saveManyUsers expected an array');
    }

	try {
		const toSave: User[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.users.length; i++) {
			const user = action.users[i];
			user.persisted = false;
			toSave.push(user);
		}

		const addResult = yield call(idb.add, 'user', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyUsers: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.USER_SAVE_MANY_FAILURE, users: action.users, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.USER_SAVE_MANY, users: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.USER_SAVE_MANY_FAILURE, users: action.users, status: 500, err});

		console.error(`Error saving ${action.users.length} users to database`, err);
	}
}

/**
 * @param {UserAction} action
 */
export function* removeUser(action: UserAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.user === undefined || action.user === null || !(action.user instanceof User)) {
			console.error('removeUser expected type User, received', action.user);
			throw new Error('removeUser expected type User');
		}

	try {
		// Mark as deleted
		action.user.status = 'D';
		action.user.addedOn = newHRTimestamp();
		action.user.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'user', action.user);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeUser: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.USER_REMOVE_FAILURE, user: action.user, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.USER_REMOVE, user: action.user});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.USER_REMOVE_FAILURE, user: action.user, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this User.
	*
	* @param {UserAction} action
	*/
export function* restoreUser(action: UserAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreUser expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.user === undefined || action.user === null) {
        console.error('restoreUser expected a User, received null');
        throw new Error('restoreUser expected a User, received null');
    }
    else if (!(action.user instanceof User)) action.user = new User(action.user); 
    
	try {
		// Mark as restored
		action.user.status = 'U';
		action.user.addedOn = newHRTimestamp();
		action.user.addedBy = userId;
		action.user.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'user', action.user);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreUser: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.USER_RESTORE_FAILURE, user: action.user, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.USER_RESTORE, user: action.user});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.USER_RESTORE_FAILURE, user: action.user, status: 500, err});

		console.error('Error restoring from database', err);
	}
}